import { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Truck, Zap, Package, AlertCircle, Store } from 'lucide-react';
import { useDeliveryRates } from '@/hooks/useDeliveryRates';
import { isValidStateCity } from '@/lib/nigeriaLocations';

// Fez Delivery Logo Component
const FezLogo = ({ className = "h-5 w-auto" }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 80 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="80" height="24" rx="4" fill="#FF6B00" />
    <text 
      x="40" 
      y="17" 
      textAnchor="middle" 
      fill="white" 
      fontFamily="Arial, sans-serif" 
      fontWeight="bold" 
      fontSize="14"
    >
      FEZ
    </text>
  </svg>
);

interface DeliveryRate {
  id: string;
  name: string;
  type: 'regular' | 'express' | 'faramove';
  price: number;
  currency: string;
  estimatedDays: string;
  description: string;
  isSelfDelivery?: boolean;
  provider?: 'fez' | 'self';
}

interface DeliveryOptionsProps {
  sellerId: string;
  sellerState?: string;
  sellerAddress?: string;
  destinationState: string;
  destinationCity: string;
  destinationAddress?: string;
  items?: Array<{
    quantity: number;
    price: number;
    title: string;
  }>;
  onSelectOption: (option: DeliveryRate) => void;
  selectedOptionId?: string;
}

// Fallback rate when API pricing is unavailable
const FALLBACK_FEZ_RATE: DeliveryRate = {
  id: 'fez-fallback',
  name: 'Fez Delivery',
  type: 'regular',
  price: 2500,
  currency: 'NGN',
  estimatedDays: '3-5 business days',
  description: 'Estimated delivery fee',
  provider: 'fez',
};

export function DeliveryOptions({
  sellerId,
  sellerState = 'Lagos',
  sellerAddress = '',
  destinationState,
  destinationCity,
  destinationAddress = '',
  items = [],
  onSelectOption,
  selectedOptionId
}: DeliveryOptionsProps) {
  const isValidLocation = isValidStateCity(destinationState, destinationCity);

  // Fez delivery is the default for ALL sellers.
  // Sellers can later add their own delivery options via their dashboard.

  const { data, isLoading, isError, error } = useDeliveryRates({
    sellerId,
    destinationState,
    destinationCity,
    destinationAddress,
    items,
  });

  const rateSource = data?.source || 'unknown';
  // Never show "Estimated" for fez-api or fez-matrix rates — they are reliable calculations
  const isEstimated = false;

  const rates = useMemo<DeliveryRate[]>(() => {
    const raw = data?.rates || [];
    const mapped = raw.map((r) => ({
      id: r.id,
      name: r.name,
      type: r.type,
      price: r.price,
      currency: r.currency,
      estimatedDays: r.estimatedDays,
      description: r.description,
      provider: (r.provider === 'self-delivery' ? 'self' : 'fez') as 'fez' | 'self',
    }));

    console.log('[DeliveryOptions] API rates:', mapped.length, 'source:', rateSource, 'isError:', isError);

    // Use API rates if available
    if (mapped.length > 0) {
      return mapped;
    }

    // FALLBACK: Always show Fez fallback rate when no API rates returned
    if (isValidLocation && !isLoading) {
      console.log('[DeliveryOptions] Using fallback Fez rate (₦2,500)');
      return [FALLBACK_FEZ_RATE];
    }

    return [];
  }, [data?.rates, rateSource, isValidLocation, isLoading, isError]);

  useEffect(() => {
    if (!isValidLocation) return;
    if (rates.length === 0) return;
    const current = selectedOptionId ? rates.find((r) => r.id === selectedOptionId) : undefined;
    if (!current) onSelectOption(rates[0]);
  }, [isValidLocation, rates, selectedOptionId, onSelectOption]);

  const getIcon = (type: string, provider?: 'fez' | 'self') => {
    if (provider === 'self') {
      return <Store className="h-5 w-5 text-primary" />;
    }
    return type === 'express' 
      ? <Zap className="h-5 w-5 text-amber-500" />
      : <Truck className="h-5 w-5 text-primary" />;
  };

  const getProviderBadge = (provider?: 'fez' | 'self') => {
    if (provider === 'fez') {
      return (
        <span className="flex items-center gap-1">
          <FezLogo className="h-4 w-auto" />
          {isEstimated && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">
              Estimated
            </Badge>
          )}
        </span>
      );
    }
    if (provider === 'self') {
      return (
        <Badge variant="outline" className="text-xs border-primary/30 text-primary">
          Seller Delivery
        </Badge>
      );
    }
    return null;
  };

  if (!isValidLocation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            Delivery Options
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Select a valid <span className="font-medium">State</span> and <span className="font-medium">City</span> to calculate delivery.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            Calculating Delivery Options...
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Package className="h-5 w-5" />
          Delivery Options
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isError && rates.length > 0 && rates[0]?.id === 'fez-fallback' && (
          <div className="flex items-center gap-2 text-amber-600 text-sm mb-4 p-2 bg-amber-50 rounded">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Using estimated pricing. Final fee may vary slightly.</span>
          </div>
        )}

        <RadioGroup
          value={selectedOptionId || rates[0]?.id}
          onValueChange={(value) => {
            const selected = rates.find(r => r.id === value);
            if (selected) onSelectOption(selected);
          }}
          className="space-y-3"
        >
          {rates.map((rate) => (
            <div key={rate.id}>
              <Label
                htmlFor={rate.id}
                className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedOptionId === rate.id 
                    ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={rate.id} id={rate.id} />
                  <div className="flex items-center gap-2">
                    {getIcon(rate.type, rate.provider)}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{rate.name}</span>
                        {rate.type === 'express' && (
                          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                            Fastest
                          </Badge>
                        )}
                        {getProviderBadge(rate.provider)}
                      </div>
                      <p className="text-sm text-muted-foreground">{rate.estimatedDays}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className="font-bold text-lg">₦{rate.price.toLocaleString()}</span>
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>

        {rates.length === 0 && !isLoading && (
          <div className="text-center py-4 text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No delivery options available for this location</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DeliveryOptions;
